/**
 * components/MappingCanvas.tsx - Mapping Canvas Component
 * 
 * CHANGE HISTORY:
 * - 251103_1045 [pending] danielbeer: Initial MappingCanvas component
 * 
 * Displays visual connection lines between mapped fields.
 */

import React from 'react';
import type { Field, Mapping } from '../types';

export interface MappingCanvasProps {
  mappings: Mapping[];
  sourceFields: Field[];
  targetFields: Field[];
  selectedMappings: string[];
  onMappingClick: (mapping: Mapping) => void;
  onMappingRemove: (mappingId: string) => void;
  showConfidence?: boolean;
  theme?: 'light' | 'dark';
}

export const MappingCanvas: React.FC<MappingCanvasProps> = ({
  mappings,
  sourceFields,
  targetFields,
  selectedMappings,
  onMappingClick,
  onMappingRemove,
  showConfidence = true,
  theme = 'light'
}) => {
  return (
    <div className={`mapping-canvas mapping-canvas--${theme}`}>
      <svg className="mapping-canvas__svg" width="100%" height="100%">
        {mappings.filter(m => m.enabled).map(mapping => {
          const isSelected = selectedMappings.includes(mapping.id);
          
          return (
            <g
              key={mapping.id}
              className={`mapping-line ${isSelected ? 'mapping-line--selected' : ''}`}
              onClick={() => onMappingClick(mapping)}
            >
              {/* Connection line */}
              <line
                x1="0%"
                y1="50%"
                x2="100%"
                y2="50%"
                stroke={isSelected ? '#646cff' : '#999'}
                strokeWidth={isSelected ? 3 : 2}
                strokeDasharray={mapping.validated ? 'none' : '5,5'}
              />
              
              {/* Confidence indicator */}
              {showConfidence && mapping.confidence && (
                <text
                  x="50%"
                  y="50%"
                  textAnchor="middle"
                  fill={theme === 'dark' ? '#fff' : '#000'}
                  fontSize="12"
                >
                  {mapping.confidence}%
                </text>
              )}
              
              {/* Remove button */}
              {isSelected && (
                <circle
                  cx="50%"
                  cy="50%"
                  r="12"
                  fill="#ff4444"
                  cursor="pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMappingRemove(mapping.id);
                  }}
                />
              )}
            </g>
          );
        })}
      </svg>
      
      {mappings.length === 0 && (
        <div className="mapping-canvas__empty">
          <p>No mappings yet</p>
          <p className="mapping-canvas__hint">
            Drag fields from source to target to create mappings
          </p>
        </div>
      )}
    </div>
  );
};
